import { useState, useCallback, useRef, useEffect } from 'react';
import clsx from 'clsx';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragMoveEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { useTripStore } from '../store/tripStore';
import { useLibraryStore } from '../store/libraryStore';
import { useUIStore } from '../store/uiStore';
import { DayColumn } from './DayColumn';
import { formatDateRange } from '../utils/format';
import { Sidebar } from './Sidebar';
import { DragOverlayCard } from './DragOverlayCard';
import { POIEditorModal } from './POIEditorModal';
import { AccommodationConfigModal } from './AccommodationConfigModal';
import { TransportEditorModal } from './TransportEditorModal';
import { ActivityFeed } from './ActivityFeed';
import { DayFocusView } from './DayFocusView';
import { useUndoKeyboard } from '../hooks/useUndoKeyboard';
import { useTripSidebarController } from '../hooks/useTripSidebarController';
import { dndIds } from '../utils/dndIds';
import type { POI, Day, AccommodationConfig } from '@poi-planner/shared';

// Compact date range picker for the nav bar
function TripDatePicker({ firstDate, lastDate }: { firstDate: string; lastDate: string }) {
  const [isEditing, setIsEditing] = useState(false);
  const [dateValue, setDateValue] = useState(firstDate);
  const inputRef = useRef<HTMLInputElement>(null);
  const setTripStartDate = useTripStore((s) => s.setTripStartDate);
  const isReadOnly = useTripStore((s) => s.isReadOnly);

  useEffect(() => {
    if (firstDate && !isEditing) {
      setDateValue(firstDate);
    }
  }, [firstDate, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleCommit = useCallback(() => {
    if (dateValue && dateValue !== firstDate) {
      setTripStartDate(dateValue);
    }
    setIsEditing(false);
  }, [dateValue, firstDate, setTripStartDate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCommit();
    } else if (e.key === 'Escape') {
      setDateValue(firstDate);
      setIsEditing(false);
    }
  }, [handleCommit, firstDate]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="date"
        value={dateValue}
        onChange={(e) => setDateValue(e.target.value)}
        onBlur={handleCommit}
        onKeyDown={handleKeyDown}
        className="px-2 py-1 bg-white border-2 border-blue-500 rounded-lg text-[13px] font-medium text-text-primary outline-none"
      />
    );
  }

  return (
    <button
      onClick={() => !isReadOnly() && setIsEditing(true)}
      disabled={isReadOnly()}
      className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-medium text-slate-600 transition-colors ${
        isReadOnly() ? 'bg-slate-100 cursor-default' : 'bg-slate-100 hover:bg-slate-200'
      }`}
      title={isReadOnly() ? 'Read-only mode' : 'Click to edit start date'}
    >
      <span className="material-symbols-outlined text-base text-slate-500">
        calendar_today
      </span>
      <span>{formatDateRange(firstDate, lastDate)}</span>
      <span className="material-symbols-outlined text-sm text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
        edit
      </span>
    </button>
  );
}

// Add Day placeholder column
function AddDayColumn({ onAddDay }: { onAddDay: () => void }) {
  return (
    <button
      onClick={onAddDay}
      className="group min-w-[var(--spacing-column-width)] max-w-[var(--spacing-column-width)] h-[280px] rounded-2xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 flex flex-col items-center justify-center gap-3 transition-all cursor-pointer"
    >
      <div className="size-12 rounded-full bg-slate-100 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
        <span className="material-symbols-outlined text-2xl text-slate-400 group-hover:text-blue-500 transition-colors">
          add
        </span>
      </div>
      <span className="text-sm font-medium text-slate-400 group-hover:text-blue-500 transition-colors">
        Add Day
      </span>
    </button>
  );
}

interface DragData {
  type: 'sidebar-poi' | 'scheduled-poi' | 'day-column';
  poiId?: string;
  dayId?: string;
  dayIndex?: number;
}

export interface InsertIndicator {
  dayId: string;
  index: number;
}

// Collision detection: prefer specific sortable cards over broad containers
const customCollisionDetection: CollisionDetection = (args) => {
  const pw = pointerWithin(args);
  if (pw.length > 0) {
    // Prefer sortable items (dayId:poiId format) over containers
    const cards = pw.filter((c) => dndIds.isDayItem(String(c.id)));
    if (cards.length > 0) return cards;
    return pw;
  }
  return closestCorners(args);
};

// Compute insertion index by comparing pointer Y with card DOM positions
function computeInsertIndex(dayId: string, pointerY: number): number {
  const state = useTripStore.getState();
  const day = state.schedule.days.find((d) => d.id === dayId);
  if (!day || day.scheduledPOIs.length === 0) return 0;

  const sorted = [...day.scheduledPOIs].sort((a, b) => a.order - b.order);

  for (let i = 0; i < sorted.length; i++) {
    const el = document.querySelector(`[data-poi-card="${dayId}:${sorted[i].poiId}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      if (pointerY < center) return i;
    }
  }

  return sorted.length;
}

// Sortable wrapper for day columns
function SortableDayColumn({
  day,
  dayIndex,
  ...props
}: {
  day: Day;
  dayIndex: number;
  previousDay: Day | null;
  accommodationPOIs: POI[];
  accommodationConfigs: Record<string, AccommodationConfig>;
  pois: POI[];
  transportEvents: any[];
  getAccommodation: (id: string) => POI | undefined;
  getPOI: (id: string) => POI | undefined;
  getNightCount: (dayIndex: number) => { current: number; total: number } | null;
  onDeleteDay: (dayId: string) => void;
  insertIndicator: number | null;
  onEnterFocus?: (dayId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: dndIds.dayColumn(day.id),
    data: { type: 'day-column', dayIndex } as DragData,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <DayColumn
        day={day}
        dayIndex={dayIndex}
        {...props}
      />
    </div>
  );
}

// Simple day column overlay for drag preview
function DayColumnOverlay({ day, dayIndex }: { day: Day; dayIndex: number }) {
  return (
    <div className="min-w-[var(--spacing-column-width)] max-w-[var(--spacing-column-width)] h-[200px] rounded-2xl bg-white border-2 border-blue-500 shadow-[0_20px_40px_rgba(0,0,0,0.15)] flex flex-col items-center justify-center gap-2">
      <span className="material-symbols-outlined text-[32px] text-blue-500">
        drag_indicator
      </span>
      <span className="text-base font-bold text-slate-800">
        Day {dayIndex + 1}
      </span>
      <span className="text-xs text-slate-500">
        {day.date}
      </span>
    </div>
  );
}

export function KanbanBoard() {
  const navigate = useNavigate();
  const {
    trip,
    schedule,
    permission,
    isReadOnly,
    getAccommodation,
    getPOI,
    getNightCount,
    schedulePOI,
    unschedulePOI,
    movePOI,
    reorderPOIs,
    reorderDays,
    saveStatus,
    addDay,
    deleteDay,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useTripStore();

  const { pois, getAccommodationPOIs } = useLibraryStore();
  const accommodationPOIs = getAccommodationPOIs();
  const accommodationConfigs = schedule.accommodationConfigs;
  const sidebarController = useTripSidebarController(pois, schedule);

  useUndoKeyboard();

  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const focusedDayId = useUIStore((s) => s.focusedDayId);
  const enterDayFocus = useUIStore((s) => s.enterDayFocus);

  const [activeDragPOI, setActiveDragPOI] = useState<POI | null>(null);
  const [activeDragDay, setActiveDragDay] = useState<{ day: Day; index: number } | null>(null);
  const [activeDragData, setActiveDragData] = useState<DragData | null>(null);
  const [insertIndicator, setInsertIndicator] = useState<InsertIndicator | null>(null);
  const pointerYRef = useRef(0);

  // Track pointer Y position continuously
  useEffect(() => {
    const handler = (e: PointerEvent) => {
      pointerYRef.current = e.clientY;
    };
    window.addEventListener('pointermove', handler, { passive: true });
    return () => window.removeEventListener('pointermove', handler);
  }, []);

  // Disable drag-and-drop sensors in read-only mode
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
      disabled: isReadOnly(),
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      disabled: isReadOnly(),
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const data = event.active.data.current as DragData | undefined;
    if (!data) return;
    setActiveDragData(data);

    if (data.type === 'day-column' && data.dayIndex !== undefined) {
      const day = useTripStore.getState().schedule.days[data.dayIndex];
      setActiveDragDay(day ? { day, index: data.dayIndex } : null);
      setActiveDragPOI(null);
    } else if (data.poiId) {
      const poi = useTripStore.getState().getPOI(data.poiId);
      setActiveDragPOI(poi ?? null);
      setActiveDragDay(null);
    }
  }, []);

  // Update insertion indicator during drag
  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { over, active } = event;
    if (!over) {
      setInsertIndicator(null);
      return;
    }

    const activeData = active.data.current as DragData | undefined;
    if (!activeData) return;

    const overId = String(over.id);
    let targetDayId: string | null = null;

    if (dndIds.isDayContainer(overId)) {
      targetDayId = dndIds.parseDayContainer(overId);
    } else if (dndIds.isDayItem(overId)) {
      targetDayId = dndIds.parseDayItem(overId).dayId;
    }

    if (!targetDayId) {
      setInsertIndicator(null);
      return;
    }

    // Don't show indicator for same-day reorder (sortable handles visuals)
    if (activeData.type === 'scheduled-poi' && activeData.dayId === targetDayId) {
      setInsertIndicator(null);
      return;
    }

    const index = computeInsertIndex(targetDayId, pointerYRef.current);
    setInsertIndicator((prev) => {
      if (prev && prev.dayId === targetDayId && prev.index === index) return prev;
      return { dayId: targetDayId!, index };
    });
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragPOI(null);
    setActiveDragDay(null);
    setActiveDragData(null);
    setInsertIndicator(null);

    if (!over) return;

    const activeData = active.data.current as DragData | undefined;
    if (!activeData) return;

    const overId = String(over.id);

    // Handle day column reordering
    if (activeData.type === 'day-column' && activeData.dayIndex !== undefined) {
      const overData = over.data.current as DragData | undefined;
      if (overData?.type === 'day-column' && overData.dayIndex !== undefined) {
        if (activeData.dayIndex !== overData.dayIndex) {
          reorderDays(activeData.dayIndex, overData.dayIndex);
        }
      }
      return;
    }

    // Dropped on sidebar → unschedule
    if (overId === dndIds.sidebar) {
      if (activeData.type === 'scheduled-poi' && activeData.dayId && activeData.poiId) {
        unschedulePOI(activeData.poiId, activeData.dayId);
      }
      return;
    }

    // Determine target day
    let targetDayId: string | null = null;
    if (dndIds.isDayContainer(overId)) {
      targetDayId = dndIds.parseDayContainer(overId);
    } else if (dndIds.isDayItem(overId)) {
      targetDayId = dndIds.parseDayItem(overId).dayId;
    }

    if (!targetDayId || !activeData.poiId) return;

    // Compute insertion index from pointer Y position
    const targetIndex = computeInsertIndex(targetDayId, pointerYRef.current);

    if (activeData.type === 'sidebar-poi') {
      // Sidebar → Day: schedule at computed position
      schedulePOI(activeData.poiId, targetDayId, targetIndex);
    } else if (activeData.type === 'scheduled-poi' && activeData.dayId) {
      if (activeData.dayId === targetDayId) {
        // Same day: reorder
        const day = useTripStore.getState().schedule.days.find((d) => d.id === targetDayId);
        if (day) {
          const sorted = [...day.scheduledPOIs].sort((a, b) => a.order - b.order);
          const oldIndex = sorted.findIndex((sp) => sp.poiId === activeData.poiId);
          if (oldIndex !== -1 && oldIndex !== targetIndex) {
            // Adjust for the fact that the active item is still in the list
            const adjustedTarget = targetIndex > oldIndex ? targetIndex - 1 : targetIndex;
            if (oldIndex !== adjustedTarget) {
              reorderPOIs(targetDayId, oldIndex, adjustedTarget);
            }
          }
        }
      } else {
        // Different day: move
        movePOI(activeData.poiId, activeData.dayId, targetDayId, targetIndex);
      }
    }
  }, [schedulePOI, unschedulePOI, movePOI, reorderPOIs, reorderDays]);

  if (!trip) return null;

  // Render DayFocusView when a day is focused
  if (focusedDayId) {
    return <DayFocusView />;
  }

  const days = schedule.days;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col h-screen w-full overflow-hidden">
        {/* Top Navigation Bar */}
        <header className="flex items-center justify-between whitespace-nowrap border-b border-border bg-white px-6 py-3 z-10 shrink-0">
          {/* Left: Back button + Sidebar toggle + Trip context */}
          <div className="flex items-center gap-4">
            {/* Back to trips list */}
            <button
              onClick={() => navigate('/')}
              className="btn-icon"
              title="Back to trips"
            >
              <span className="material-symbols-outlined text-lg">
                arrow_back
              </span>
            </button>

            <div className="w-px h-6 bg-slate-200" />

            <button
              onClick={toggleSidebar}
              className="btn-icon"
              title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
            >
              <span className="material-symbols-outlined text-lg">
                {sidebarCollapsed ? 'right_panel_open' : 'left_panel_close'}
              </span>
            </button>

            <div className="w-px h-6 bg-slate-200" />

            {/* Trip name */}
            <h1 className="text-lg font-bold text-slate-800 tracking-tight">
              {trip.name}
            </h1>

            {/* Date range picker */}
            {days.length > 0 && days[0].date && (
              <TripDatePicker
                firstDate={days[0].date}
                lastDate={days[days.length - 1].date}
              />
            )}
          </div>

          {/* Right: Utilities + Brand */}
          <div className="flex items-center gap-3">
            {!isReadOnly() && (
              <UndoRedoButtons
                canUndo={canUndo()}
                canRedo={canRedo()}
                onUndo={undo}
                onRedo={redo}
              />
            )}
            <ActivityFeed />
            <SaveStatusIndicator status={saveStatus} />

            <div className="w-px h-6 bg-slate-200 ml-1" />

            {/* POI Planner brand (subtle) */}
            <div className="flex items-center gap-2 text-slate-400">
              <svg width="14" height="14" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M44 11.2727C44 14.0109 39.8386 16.3957 33.69 17.6364C39.8386 18.877 44 21.2618 44 24C44 26.7382 39.8386 29.123 33.69 30.3636C39.8386 31.6043 44 33.9891 44 36.7273C44 40.7439 35.0457 44 24 44C12.9543 44 4 40.7439 4 36.7273C4 33.9891 8.16144 31.6043 14.31 30.3636C8.16144 29.123 4 26.7382 4 24C4 21.2618 8.16144 18.877 14.31 17.6364C8.16144 16.3957 4 14.0109 4 11.2727C4 7.25611 12.9543 4 24 4C35.0457 4 44 7.25611 44 11.2727Z" fill="currentColor" />
              </svg>
              <span className="text-xs font-medium tracking-wide">POI Planner</span>
            </div>
          </div>
        </header>

        {/* Read-Only Mode Banner */}
        {isReadOnly() && (
          <div className="flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-50 border-b border-blue-200 text-blue-900 text-sm">
            <span className="material-symbols-outlined text-base">visibility</span>
            <span className="font-medium">
              Viewing shared trip - You have read-only access
            </span>
            {permission === 'viewer' && (
              <span className="text-xs text-blue-700">
                (Contact the owner for edit access)
              </span>
            )}
          </div>
        )}

        {/* Main Content */}
        <main className="flex flex-1 overflow-hidden">
          {!sidebarCollapsed && <Sidebar controller={sidebarController} />}

          {/* Day Columns */}
          <div className="custom-scrollbar flex-1 overflow-x-auto flex px-8 py-6 gap-6">
            <SortableContext
              items={days.map((d) => dndIds.dayColumn(d.id))}
              strategy={horizontalListSortingStrategy}
            >
              {days.map((day, index) => (
                <SortableDayColumn
                  key={day.id}
                  day={day}
                  dayIndex={index}
                  previousDay={index > 0 ? days[index - 1] : null}
                  accommodationPOIs={accommodationPOIs}
                  accommodationConfigs={accommodationConfigs}
                  pois={pois}
                  transportEvents={schedule.transportEvents}
                  getAccommodation={getAccommodation}
                  getPOI={getPOI}
                  getNightCount={getNightCount}
                  onDeleteDay={deleteDay}
                  insertIndicator={insertIndicator?.dayId === day.id ? insertIndicator.index : null}
                  onEnterFocus={enterDayFocus}
                />
              ))}
            </SortableContext>
            {!isReadOnly() && <AddDayColumn onAddDay={addDay} />}
          </div>
        </main>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDragPOI ? <DragOverlayCard poi={activeDragPOI} /> : null}
        {activeDragDay ? <DayColumnOverlay day={activeDragDay.day} dayIndex={activeDragDay.index} /> : null}
      </DragOverlay>

      <POIEditorModal />
      <AccommodationConfigModal />
      <TransportEditorModal />
    </DndContext>
  );
}

function UndoRedoButtons({
  canUndo,
  canRedo,
  onUndo,
  onRedo,
}: {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}) {
  return (
    <div className="flex gap-0.5">
      <button
        onClick={onUndo}
        disabled={!canUndo}
        className={clsx(
          'btn-icon',
          !canUndo && 'opacity-30 cursor-default hover:bg-transparent'
        )}
        title="Undo (Ctrl+Z)"
      >
        <span className="material-symbols-outlined text-lg">undo</span>
      </button>
      <button
        onClick={onRedo}
        disabled={!canRedo}
        className={clsx(
          'btn-icon',
          !canRedo && 'opacity-30 cursor-default hover:bg-transparent'
        )}
        title="Redo (Ctrl+Shift+Z)"
      >
        <span className="material-symbols-outlined text-lg">redo</span>
      </button>
    </div>
  );
}

function SaveStatusIndicator({ status }: { status: string }) {
  const config = {
    saved: { icon: 'cloud_done', label: 'Saved', color: '#22c55e' },
    saving: { icon: 'sync', label: 'Saving...', color: '#f59e0b' },
    unsaved: { icon: 'pending', label: 'Unsaved changes', color: '#94a3b8' },
    error: { icon: 'cloud_off', label: 'Save failed', color: '#ef4444' },
  }[status] ?? { icon: 'cloud_done', label: 'Saved', color: '#22c55e' };

  return (
    <div
      className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-all"
      style={{ color: config.color, background: `${config.color}10` }}
    >
      <span
        className={clsx('material-symbols-outlined text-base', status === 'saving' && 'animate-spin')}
      >
        {config.icon}
      </span>
      <span>{config.label}</span>
    </div>
  );
}
